import { resolvePrimitiveOperationInput } from '@/adapters/primitive/_pure/interaction/operationInput';
import { derivePrimitiveTracePayload } from '@/adapters/primitive/_pure/model/trace';
import { projectBooleanOperationTrace } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { projectNaturalDivisionTrace } from '@/adapters/primitive/_pure/derivation/natural/divisionTraceProjection';
import { projectNaturalLessThanTrace } from '@/adapters/primitive/_pure/derivation/natural/lessThanTraceProjection';
import { projectNaturalMultiplicationTrace } from '@/adapters/primitive/_pure/derivation/natural/multiplicationTraceProjection';
import { projectNaturalTraceToStepStates } from '@/adapters/primitive/_pure/derivation/natural/traceProjection';
import { deriveBooleanIdentityStepStates, deriveNaturalIdentityStepStates } from '@/adapters/primitive/_pure/derivation/identityTraceProjection';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { TraceReplay } from '@/_contract/trace';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { PrimitiveOperationDerivationOptions } from '@/adapters/primitive/_contract/natural/arithmetic';
import type { Config, PrimitiveWheelState, RuntimeState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInput } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { PrimitiveTrace, PrimitiveTracePayload, PrimitiveTraceSettings } from '@/adapters/primitive/_contract/trace';

export function toPrimitiveStepWheels(step: PrimitiveStepState): readonly PrimitiveWheelState[] {
    return step.keyframes[step.keyframes.length - 1]?.wheels ?? [];
}

export function primitiveTraceSettings(config: Config, options?: PrimitiveOperationDerivationOptions): PrimitiveTraceSettings {
    if (config.natural.radix !== 10) throw new Error('Primitive semantic traces currently require radix 10.');
    return {
        radix: 10, operandSchedulingMode: config.natural.operandSchedulingMode,
        allowExoticTerminalStates: options?.allowExoticTerminalStates ?? false,
        runawayBorrowAssemblyBudget: config.natural.runawayBudget.borrowAssemblies,
        runawayDivisionCycleBudget: config.natural.runawayBudget.divisionCycles,
    };
}

export function configForPrimitiveTrace(config: Config, trace?: PrimitiveTrace): Config {
    if (!trace) return config;
    const settings = trace.payload.settings;
    if (config.natural.radix === settings.radix && config.natural.operandSchedulingMode === settings.operandSchedulingMode) return config;
    return { ...config, natural: { ...config.natural, radix: settings.radix,
        operandSchedulingMode: settings.operandSchedulingMode } };
}

export function projectPrimitiveTracePayload(config: Config, payload: PrimitiveTracePayload,
    splitAlignmentApproach = false, divisionComparison?: 'explicit' | 'integrated',
    directionCatalog: DirectionCatalog<PrimitiveOperationDirectionEffect> | undefined = config.sceneDirectionCatalogs?.visuals,
    fullComparisons?: number): readonly PrimitiveStepState[] {
    if (config.natural.radix !== payload.settings.radix) throw new Error('Primitive projection radix does not match its trace.');
    if (divisionComparison && payload.kind !== 'division') {
        throw new Error('Division comparison requires a division trace.');
    }
    switch (payload.kind) {
        case 'natural':
            return projectNaturalTraceToStepStates({ config, ...payload.input,
                operandSchedulingMode: payload.settings.operandSchedulingMode, trace: payload.semantic,
                splitAlignmentApproach }).steps;
        case 'multiplication':
            return projectNaturalMultiplicationTrace({ config, multiplier: payload.input.operandOne,
                multiplicand: payload.input.operandTwo, trace: payload.semantic,
                directionCatalog, fullComparisons,
                splitZeroCrossing: splitAlignmentApproach, splitProductSettlement: splitAlignmentApproach });
        case 'division':
            return projectNaturalDivisionTrace({ config, dividend: payload.input.operandOne,
                divisor: payload.input.operandTwo, trace: payload.semantic, divisionComparison,
                directionCatalog, fullComparisons,
                splitRemainderSettlement: splitAlignmentApproach });
        case 'comparison':
            return projectNaturalLessThanTrace({ config, source: payload.input.source,
                target: payload.input.target, trace: payload.semantic, directionCatalog });
        case 'boolean':
            return payload.input.operation === 'logical-not'
                ? projectBooleanOperationTrace({ config, operation: payload.input.operation,
                    inputOne: payload.input.input, trace: payload.semantic, directionCatalog })
                : projectBooleanOperationTrace({ config, operation: payload.input.operation,
                    inputOne: payload.input.operandOne, inputTwo: payload.input.operandTwo, trace: payload.semantic, directionCatalog });
        case 'natural-identity':
            return deriveNaturalIdentityStepStates({ config, input: payload.semantic.value });
        case 'boolean-identity':
            return deriveBooleanIdentityStepStates({ config, input: payload.semantic.value });
    }
}

export function derivePrimitiveStepStatesFromOperationInput(args: {
    config: Config;
    input: PrimitiveOperationInput;
    options?: PrimitiveOperationDerivationOptions;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
}): readonly PrimitiveStepState[] {
    return projectPrimitiveTracePayload(args.config,
        derivePrimitiveTracePayload(args.input, primitiveTraceSettings(args.config, args.options)),
        false, undefined, args.directionCatalog);
}

export function derivePrimitiveStepsForRuntime(config: Config, state: RuntimeState): readonly PrimitiveStepState[] {
    if (state.selectedTrace) return projectPrimitiveTracePayload(configForPrimitiveTrace(config, state.selectedTrace),
        state.selectedTrace.payload, Boolean(state.episode), state.episode ? state.divisionComparison : undefined,
        state.episode ? state.sceneDirectionCatalogs?.visuals : undefined, state.episode ? state.fullComparisons : undefined);
    const input = resolvePrimitiveOperationInput(state.inputDraft);
    return input === null ? [] : derivePrimitiveStepStatesFromOperationInput({
        config, input, options: { allowExoticTerminalStates: true },
        directionCatalog: state.episode ? state.sceneDirectionCatalogs?.visuals : undefined,
    });
}

export function derivePrimitiveTraceReplay(trace: PrimitiveTrace, steps: readonly PrimitiveStepState[]): TraceReplay {
    const landmarks = steps.map((step) => ({
        id: step.stepKey, label: step.description, ...(step.moment ? { moment: step.moment } : {}),
    }));
    if (new Set(landmarks.map((landmark) => landmark.id)).size !== landmarks.length) {
        throw new Error(`Primitive trace '${trace.handle}' has ambiguous replay landmarks.`);
    }
    return { trace: { adapter: trace.adapter, traceKind: trace.traceKind,
        handle: trace.handle, schemaVersion: trace.schemaVersion }, landmarks,
        actionAnchors: steps.flatMap((step, index) => [
            ...(index === 0 ? [] : [{ landmark: step.stepKey, phase: 'start' as const, label: `Start: ${step.description}` }]),
            { landmark: step.stepKey, phase: 'complete' as const, label: `Complete: ${step.description}` },
        ]) };
}
